// src/pages/UserManagement/modals/PermissionsModal.tsx
// Modal gán Vai trò cho người dùng

import React, { useEffect, useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
} from 'reactstrap';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { User, Role } from '../types';
import { updateUserRole } from 'slices/userManagement/thunk';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('user_management');

interface PermissionsModalProps {
  isOpen: boolean;
  toggle: () => void;
  user: User | null;
  roles: Role[];
  /** Gọi lại sau khi lưu vai trò thành công — dùng để load lại danh sách người dùng ở trang hiện tại */
  onUpdated?: () => void;
}

const PermissionsModal: React.FC<PermissionsModalProps> = ({
  isOpen,
  toggle,
  user,
  roles,
  onUpdated,
}) => {
  const location = useLocation();
  const dispatch = useDispatch<any>();

  const [selectedRole, setSelectedRole] = useState<number>(user?.roleId || 0);
  const [saving, setSaving] = useState(false);

  // PermissionsModal không unmount giữa các lần mở (component cha luôn render, chỉ isOpen đổi) nên
  // useState khởi tạo selectedRole từ user?.roleId CHỈ chạy 1 lần duy nhất ở lần mount đầu tiên —
  // nếu không đồng bộ lại ở đây, mở popup cho user khác/lần sau sẽ hiện sai (hoặc "Chọn Vai Trò"
  // trống) dù user đó đã có vai trò, gây hiểu nhầm. Đồng bộ lại mỗi khi popup được mở.
  useEffect(() => {
    if (isOpen) {
      setSelectedRole(user?.roleId || 0);
    }
  }, [isOpen, user]);

  const handleRoleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedRole(Number(e.target.value));
  };

  const handleSave = async () => {
    if (!user?.id) return;

    setSaving(true);

    const result = await dispatch(updateUserRole({ id: user.id, roleId: selectedRole }));

    setSaving(false);

    if (updateUserRole.fulfilled.match(result)) {
      toast.success(st('cap_nhat_vai_tro_nguoi_dung_message'));
      toggle();
      onUpdated?.();
    } else {
      toast.error((result.payload as string) || st('co_loi_xay_ra_khi_cap_message_2'));
    }
  };

  const handleClose = () => {
    setSelectedRole(user?.roleId || 0);
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered size="lg">
      <ModalHeader toggle={handleClose}>
        {st('phan_quyen_nguoi_dung_title')} {user?.name}
      </ModalHeader>
      <Form>
        <ModalBody>
          {/* Role Selection */}
          <FormGroup>
            <Label for="role">{st('vai_tro_label')}</Label>
            <Input
              type="select"
              id="role"
              value={selectedRole}
              data-action-code="input-role"
              data-action-name="Vai trò (Phân quyền)"
              onChange={handleRoleChange}
              disabled={!PageActionPermission.canEnable(location.pathname, "input-role")}
            >
              <option value="0">{st('role_placeholder')}</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                  {role.description && ` - ${role.description}`}
                </option>
              ))}
            </Input>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={handleClose} disabled={saving}>
            {st('huy_button')}
          </Button>
          {PageActionPermission.canDisplay(location.pathname, "button-save-permissions") && (
            <Button
              color="primary"
              data-action-code="button-save-permissions"
              data-action-name="Lưu quyền hạn"
              onClick={handleSave}
              disabled={selectedRole === 0 || saving}
            >
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1"></span>
                  {st('dang_luu_text')}
                </>
              ) : (
                <>
                  <i className="ri-save-line me-1"></i>
                  {st('luu_quyen_han_text')}
                </>
              )}
            </Button>
          )}
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default PermissionsModal;
